import { Inject, Injectable, Optional } from '@angular/core';
import { distinctUntilChanged, filter, map, share, switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of } from 'rxjs';
import { isEqual } from 'lodash-es';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TrackingIntegrationService } from '@nimbus-library/features/tracking/services/tracking-integration.service';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { NimbusPermissionSet } from '@nimbus-global/features/permissions/models/enums/nimbus-permission-set.enum';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { PermissionsModel } from '@nimbus-global/features/permissions/models/permissions.model';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { UserHashService } from '@nimbus-global/features/permissions/services/domain/user-hash.service';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { PAGE_USAGE_HOST } from '@nimbus-library/features/tracking/models/page-usage-host.constants';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class TrackingService {
  private readonly _isTrackingInitialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public get isTrackingInitialized$(): Observable<boolean> {
    return this._isTrackingInitialized$.asObservable();
  }

  public constructor(
    private readonly authenticationService: IAuthenticationService,
    private readonly featureFlippingService: FeatureFlippingService,
    private readonly permissionManagerService: IPermissionManagerService,
    private readonly trackingIntegrationService: TrackingIntegrationService,
    private readonly userHashService: UserHashService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    @Optional() @Inject(IMicrosoftTeamsContextAccessor) private readonly teamsContextAccessor: IMicrosoftTeamsContextAccessor | null = null,
  ) {}

  public initialize(): void {
    const initializeTrackingTool$ = this.initializeTrackingTool$();
    initializeTrackingTool$
      .pipe(
        tap((x) => this._isTrackingInitialized$.next(x)),
        untilDestroyed(this),
      )
      .subscribe();
    initializeTrackingTool$
      .pipe(
        filter((x) => x),
        switchMap(() => this.checkForModuleUpdate$()),
        untilDestroyed(this),
      )
      .subscribe();
    initializeTrackingTool$
      .pipe(
        filter((x) => x),
        switchMap(() => this.checkForUserUpdate$()),
        untilDestroyed(this),
      )
      .subscribe();
    this.authenticationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        switchMap((isAuthenticated) => (isAuthenticated ? this.checkForPageUsageUpdate$() : EMPTY)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private initializeTrackingTool$(): Observable<boolean> {
    return combineLatest([
      this.authenticationService.isAuthenticated$,
      this.permissionManagerService.getGlobalPermissionsModel$().pipe(map((model) => model?.tenant)),
    ]).pipe(
      distinctUntilChanged(isEqual),
      switchMap(([isAuthenticated, tenant]) => {
        if (!isAuthenticated || !tenant || !tenant.crmId) {
          this.trackingIntegrationService.stop();
          return of([false]);
        }

        const userInfo = this.authenticationService.getUserInfo();

        return combineLatest([
          of(tenant),
          this.featureFlippingService.isEnabledInFeatureManagementByTenantId$(userInfo.tenantId, Feature.PlanHat),
        ]);
      }),
      switchMap(([tenant, planHatEnabled]) => {
        if (!tenant) {
          return of(false);
        }

        const userInfo = this.authenticationService.getUserInfo();
        const trackingUser$ = tenant.allowToSharePii
          ? of({ id: userInfo.userName, name: userInfo.name })
          : this.userHashService.getMyHash$().pipe(map((x) => ({ id: x, name: x })));

        return planHatEnabled
          ? trackingUser$.pipe(
              switchMap((trackingUser) =>
                this.trackingIntegrationService.start({ id: tenant.crmId, name: tenant.name }, trackingUser, this.getModule()),
              ),
            )
          : of(false);
      }),
      share(),
    );
  }

  private checkForModuleUpdate$(): Observable<void> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.getModule()),
      filter((module) => !!module),
      map((module) => {
        this.trackingIntegrationService.trackModule(module);
      }),
    );
  }

  private checkForPageUsageUpdate$(): Observable<void> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => ({ module: this.getModule(), routeUrl: this.getRouteTemplate() })),
      filter((navigation) => !!navigation.module && !!navigation.routeUrl),
      distinctUntilChanged(isEqual),
      map((navigation) => {
        const userInfo = this.authenticationService.getUserInfo();
        const globalPermissionsModel = this.permissionManagerService.getGlobalPermissionsModel();
        const permissionsModel = this.permissionManagerService.getPermissionsModel();
        const hostContext = this.getHostContext();

        this.trackingIntegrationService.trackPageUsage(navigation.module, navigation.routeUrl, {
          tenantId: userInfo?.tenantId,
          userId: userInfo?.userId,
          userRoles: permissionsModel ? this.getUserRoles(permissionsModel) : new Map<string, string>(),
          crmId: globalPermissionsModel?.tenant?.crmId,
          host: hostContext.host,
          hostClientType: hostContext.hostClientType,
        });
      }),
    );
  }

  /**
   * Resolves the embedding host from the already-resolved cached Teams accessor. Portal/Admin run
   * either standalone in a browser or embedded as a Microsoft Teams tab. Never blocks or triggers a
   * fresh Teams SDK initialize: isStandAlone() is synchronous and the client-info observable is read
   * with a synchronous peek, so an unresolved value simply yields an undefined client type.
   */
  private getHostContext(): { host: string; hostClientType?: string } {
    try {
      if (!this.teamsContextAccessor || this.teamsContextAccessor.isStandAlone()) {
        return { host: PAGE_USAGE_HOST.standalone };
      }

      let hostClientType: string | undefined;
      this.teamsContextAccessor
        .getClientInfo$()
        .pipe(take(1), untilDestroyed(this))
        .subscribe((clientType) => (hostClientType = clientType));

      return { host: PAGE_USAGE_HOST.teams, hostClientType };
    } catch {
      return { host: PAGE_USAGE_HOST.standalone };
    }
  }

  private checkForUserUpdate$(): Observable<Map<string, string>> {
    return this.permissionManagerService.getPermissionsModel$().pipe(
      filter((permissionsModel) => !!permissionsModel),
      map((permissionsModel: PermissionsModel) => this.getUserRoles(permissionsModel)),
      distinctUntilChanged(isEqual),
      map((userRoles: Map<string, string>) => {
        this.trackingIntegrationService.updateUser(userRoles);
        return userRoles;
      }),
    );
  }

  private getUserRoles(permissionsModel: PermissionsModel): Map<string, string> {
    const userRoles = new Map<string, string>();
    Object.values(NimbusPermissionSet).forEach((permissionSet) =>
      userRoles.set(
        `Role_${Object.keys(NimbusPermissionSet)[Object.values(NimbusPermissionSet).indexOf(permissionSet)]}`,
        permissionsModel.permissionSets.some((p) => p.permissionSet === permissionSet) ? 'yes' : 'no',
      ),
    );
    return userRoles;
  }

  private getModule(): string {
    let routeChild = this.activatedRoute.firstChild;

    while (routeChild) {
      const trackingModule = routeChild.snapshot.data ? routeChild.snapshot.data['trackingModule'] : null;
      if (trackingModule) {
        return trackingModule;
      } else if (routeChild.firstChild) {
        routeChild = routeChild.firstChild;
      } else {
        return null;
      }
    }

    return null;
  }

  private getRouteTemplate(): string {
    const routePaths = [];
    let routeChild = this.activatedRoute.firstChild;

    while (routeChild) {
      const routePath = routeChild.snapshot.routeConfig?.path;
      if (routePath) {
        routePaths.push(routePath);
      }

      routeChild = routeChild.firstChild;
    }

    return routePaths.length ? `/${routePaths.join('/')}` : null;
  }
}
